import React from 'react';
import _ from 'lodash';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Box, InputBase, makeStyles, Typography, withStyles } from '@material-ui/core';
import { DropDownIcon } from '../Icon';
import { classNames, initValue } from '../../utils';

const useStyles = makeStyles(() => ({
  root: {
    flex: 1,
  },
  inputBox: (props) => ({
    position: 'relative',
    width: props.width ? props.width : '100%',
    background: props.highlight ?? '#fff',
    cursor: 'pointer',
    height: props.height ? props.height : '3.5rem',
    border: props.border ? props.border : 'solid 1px #e4e7eb',
    borderRadius: '0.625rem',
  }),
  inputBase: (props) => ({
    borderRadius: '0.625rem',
    height: '100%',
    width: '90%',
    lineHeight: props.height ? `${props.height}rem` : '3.5rem',
    paddingLeft: '1.25rem',
    caretColor: 'transparent',
    cursor: 'pointer',
    '& .MuiInputBase-input': {
      fontSize: '1rem',
      color: '#2e4469',
      fontFamily: 'NotoSans-Medium',
      cursor: 'pointer',
      height: 'unset',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    },
  }),
  dropDownIcon: {
    position: 'absolute',
    top: '50%',
    right: '1rem',
    transform: 'translateY(-50%)',
    height: '0.875rem',
    width: '0.875rem',
    color: '#538fff',
  },
  disabled: {
    background: '#f5f5f5 !important',
    '& ::-webkit-input-placeholder': {
      /* WebKit browsers */ color: '#c0c6cb',
    },

    '& ::-moz-placeholder': {
      /* Mozilla Firefox 19+ */ color: '#c0c6cb',
    },

    '& :-ms-input-placeholder': {
      /* Internet Explorer 10+ */ color: '#c0c6cb',
    },
  },
  iconDisabled: {
    color: '#C0C6CB',
  },
  error: {
    borderColor: '#f44336 !important',
  },
  errorMsg: {
    margin: '3px 0.825rem 0 0.825rem',
    color: '#f44336',
    fontFamily: 'NotoSans-Medium',
    fontWeight: '500',
  },
  colorIcon: {
    width: '1.375rem',
    minWidth: '1.375rem',
    height: '1.375rem',
    minHeight: '1.375rem',
    borderRadius: '4px',
    marginRight: '0.625rem',
  },
  highlight: (props) => ({
    backgroundColor: props.highlight,
  }),
}));

function AutoCompleteSelect({
  id,
  classes,
  itemList,
  className,
  placeholder,
  value,
  onChange,
  width,
  height,
  style,
  onOpen,
  disabled,
  error,
  errorMsg,
  onBlur,
  type,
  border,
  StartAdornment = <div />,
  colorIconBorder = 'unset',
  renderOption,
  customizeInput = {},
  highlight,
  whiteSpace = false,
  ...others
}) {
  const styles = useStyles({ width, height, border, highlight });

  const [inputValue, setInputValue] = React.useState('');
  const valueCtr = () => {
    const result =
      Array.isArray(itemList) &&
      itemList.find((item) => {
        return _.isEqual(item.value, value);
      });
    return initValue(result, null);
  };

  const inputBody = (params) => {
    const dropDownHandle = () => {
      params.inputProps.ref.current.childNodes[0].focus();
      params.inputProps.onMouseDown();
    };

    const getDisabledClass = (props) => {
      return disabled ? classNames(props, styles.disabled) : props;
    };

    const getErroeClass = (props) => {
      return error || errorMsg ? classNames(props, styles.error) : props;
    };

    const getColor =
      Array.isArray(itemList) &&
      itemList.find((item) => {
        return item.value === value;
      })?.color;

    return (
      <div ref={disabled ? null : params.InputProps.ref} className={getDisabledClass(getErroeClass(styles.inputBox))}>
        <InputBase
          {...params.inputProps}
          placeholder={placeholder}
          className={styles.inputBase}
          onChange={null}
          readOnly={true}
          type="text"
          onMouseDown={disabled ? null : params.inputProps.onMouseDown}
          multiline
          onBlur={(e) => {
            params.inputProps.onBlur(e);
            if (typeof onBlur === 'function') {
              onBlur();
            }
          }}
          startAdornment={
            type === 'color' ? (
              <div className={styles.colorIcon} style={{ backgroundColor: getColor, border: colorIconBorder }} />
            ) : (
              StartAdornment
            )
          }
          {...customizeInput}
        />
        <DropDownIcon
          className={disabled ? classNames(styles.dropDownIcon, styles.iconDisabled) : styles.dropDownIcon}
          onClick={disabled ? null : dropDownHandle}
        />
      </div>
    );
  };

  const renderOptionCtr = (option) => {
    const IeftIcon = option.leftIcon;
    return (
      <>
        {IeftIcon ? <IeftIcon /> : ''}
        <Box
          display="flex"
          minHeight="1.4rem"
          whiteSpace={whiteSpace ? 'pre' : 'normal'}
          style={{ color: option.value === -999 ? '#999' : null }}
        >
          {type === 'color' && (
            <div
              className={styles.colorIcon}
              style={{ backgroundColor: option.color, border: option.borderColor ?? colorIconBorder }}
            />
          )}
          {option.text}
        </Box>
      </>
    );
  };

  return (
    <Box display="flex" style={style} className={className} flexDirection="column">
      <Autocomplete
        id={id}
        classes={classes}
        className={styles.root}
        value={valueCtr()}
        onChange={(_event, newValue) => {
          if (typeof onChange === 'function') {
            onChange(newValue.value, newValue);
          }
        }}
        inputValue={inputValue}
        onInputChange={(_event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onOpen={() => {
          if (typeof onOpen === 'function') {
            onOpen();
          }
        }}
        options={Array.isArray(itemList) ? itemList : []}
        getOptionLabel={(option) => {
          return option.value !== -999 ? option.text : '';
        }}
        renderInput={(params) => inputBody(params)}
        renderOption={(option) => {
          return (
            <div style={{ display: 'flex' }} id={`droplist_item_${option.text}`} key={`droplist_item_${option.text}`}>
              {renderOption ? renderOption(option) : renderOptionCtr(option)}
            </div>
          );
        }}
        disabled={disabled}
        {...others}
      />
      {errorMsg ? <Typography className={styles.errorMsg}>{errorMsg}</Typography> : ''}
    </Box>
  );
}

export default withStyles({
  paper: {
    boxShadow: '0 2px 10px 0 rgba(0, 0, 0, 0.1)',
  },
  listbox: {
    '& li[aria-selected="true"]': {
      color: 'unset',
      background: 'unset',
      cursor: 'pointer',
    },
    '& li[data-focus="true"]': {
      color: '#538fff',
      background: '#f5faff',
      cursor: 'pointer',
    },
  },
  option: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    fontWeight: '500',
    color: '#2e4469',
  },
})(AutoCompleteSelect);
